import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Row,
  Spinner
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import ReactQuill from "react-quill";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "react-quill/dist/quill.snow.css";
import helpers from "utils/helpers";

const BroadcastForm = () => {
  const { broadcastId } = useParams();
  const isEditMode = Boolean(broadcastId);

  const { t } = useTranslation();
  const { notify } = useNotification();
  const notifyRef = useRef(notify);
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingBroadcast, setLoadingBroadcast] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    htmlContent: "",
    targetRoles: [],
    customEmails: [],
    targetUsers: [], 
    sendNow: false,
    isScheduled: false,
    scheduledAt: null,
    showRecipients: false,
    useRoles: false,
    useUsers: false,
    useCustomEmails: false,
  });

  const convertUTCToLocal = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date - tzOffset);
    return localDate.toISOString().slice(0, 16);
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await userServices.getAllUsers();
        const dataObj = res?.data?.data || res?.data || res;
        const users = dataObj?.users || (Array.isArray(dataObj) ? dataObj : []);
        if (users) {
          setUsers(users);
        }
      } catch {
        notifyRef.current("error", t("broadcasts.toast.userFetchFailed", "Failed to load users"));
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [t]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const res = await userServices.getAllRoles();
        const rolesList = res?.data?.roles || (Array.isArray(res?.data) ? res?.data : res?.roles) || [];
        if (rolesList) {
          setRoles(rolesList);
        }
      } catch {
        notifyRef.current("error", t("broadcasts.toast.rolesFetchFailed", "Failed to load roles"));
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRoles();
  }, [t]);

  useEffect(() => {
    if (!isEditMode) return;

    const loadBroadcast = async () => {
      try {
        const res = await userServices.getBroadcastById(broadcastId);
        const broadcast = res?.data || res;
        if (broadcast) {
          setFormData({
            title: broadcast.title,
            subject: broadcast.subject,
            htmlContent: broadcast.htmlContent,
            targetRoles: broadcast.targetRoles || [],
            customEmails: broadcast.customEmails || [],
            targetUsers: broadcast.targetUsers || [],
            sendNow: broadcast.sendNow,
            isScheduled: broadcast.status === "scheduled",
            scheduledAt: broadcast.scheduledAt ? convertUTCToLocal(broadcast.scheduledAt) : null,
            showRecipients:
              (broadcast.targetRoles && broadcast.targetRoles.length > 0) ||
              (broadcast.targetUsers && broadcast.targetUsers.length > 0) ||
              (broadcast.customEmails && broadcast.customEmails.length > 0),
            useRoles: broadcast.targetRoles && broadcast.targetRoles.length > 0,
            useUsers: broadcast.targetUsers && broadcast.targetUsers.length > 0,
            useCustomEmails: broadcast.customEmails && broadcast.customEmails.length > 0,
          });
        }
      } catch (err) {
        const backendError = err?.response?.data?.error;
        notify(
          "error",
          backendError
            ? t(backendError)
            : err?.response?.data?.message || t("broadcasts.toast.fetchFailed", "Failed to fetch broadcasts")
        );
        navigate("/admin/broadcasts");
      } finally {
        setLoadingBroadcast(false);
      }
    };

    loadBroadcast();
  }, [broadcastId, isEditMode, navigate, notify, t]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData((prev) => {
      let updated = { ...prev };

      if (type === "checkbox") {
        updated[name] = checked;
      } else {
        updated[name] = value;
      }

      if (name === "sendNow" && checked) {
        updated.sendNow = true;
        updated.isScheduled = false;
      }

      if (name === "isScheduled" && checked) {
        updated.isScheduled = true;
        updated.sendNow = false;
      }

      if (name === "showRecipients" && !checked) {
        updated.useRoles = false;
        updated.useUsers = false;
        updated.useCustomEmails = false;
        updated.targetRoles = [];
        updated.targetUsers = [];
        updated.customEmails = [];
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.subject || !formData.htmlContent) {
      notify("error", t("broadcasts.toast.missingFields", "Please fill all required fields"));
      return;
    }

    if (!formData.showRecipients) {
      notify("error", t("broadcasts.toast.selectRecipientsRequired", "Please select recipients."));
      return;
    }

    if (!formData.useRoles && !formData.useUsers && !formData.useCustomEmails) {
      notify(
        "error",
        t("broadcasts.toast.selectAtLeastOneRecipient", "Please select at least one recipient type.")
      );
      return;
    }

    if (
      (formData.useRoles && !formData.targetRoles.length) ||
      (formData.useUsers && !formData.targetUsers.length) ||
      (formData.useCustomEmails && !formData.customEmails.length)
    ) {
      notify("error", t("broadcasts.toast.noRolesOrEmailsOrUsers", "Please select at least one role, system user, or add custom emails."));
      return;
    }

    if (formData.scheduledAt && !formData.isScheduled) {
      notify("error", t("broadcasts.toast.scheduleCheckedRequired", "Please enable scheduling if a scheduled date and time is selected."));
      return;
    }

    if (formData.isScheduled) {
      if (!formData.scheduledAt) {
        notify("error", t("broadcasts.toast.scheduleDateRequired", "Please select a schedule date and time."));
        return;
      }

      if (new Date(formData.scheduledAt) <= new Date()) {
        notify("error", t("broadcasts.toast.invalidScheduleDate", "Scheduled date and time must be in the future."));
        return;
      }
    }

    setIsSubmitting(true);

    const payload = {
      title: formData.title,
      subject: formData.subject,
      htmlContent: formData.htmlContent,
      targetType: formData.targetUsers.length
        ? "users"
        : formData.targetRoles.length
        ? "roles"
        : "all",

      targetRoles: formData.targetRoles,
      targetUsers: formData.targetUsers,
      customEmails: formData.customEmails,
      sendNow: formData.sendNow,
      isScheduled: formData.isScheduled,
      scheduledAt: formData.scheduledAt,
    };

    try {
      if (isEditMode) {
        await userServices.updateBroadcast(broadcastId, payload);
      } else {
        await userServices.createBroadcast(payload);
      }

      notify(
        "success",
        isEditMode
          ? t("broadcasts.toast.updateSuccess", "Broadcast updated successfully")
          : t("broadcasts.toast.createSuccess", "Broadcast created successfully"),
      );

      navigate("/admin/broadcasts");
    } catch (error) {
      const backendError = error?.response?.data?.error;
      notify(
        "error",
        backendError
          ? t(backendError)
          : error?.response?.data?.message ||
            (isEditMode
              ? t("broadcasts.toast.updateFailed", "Failed to update broadcast")
              : t("broadcasts.toast.createFailed", "Failed to create broadcast")),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingBroadcast) {
    return (
      <Container className="mt-4" fluid>
        <div className="text-center">{t("common.loading", "Loading....")}</div>
      </Container>
    );
  }

  return (
    <Container className="mt-4" fluid>
      <Row>
        <Col xl="12">
          <Card className="shadow">
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">
                    {isEditMode ? t("broadcasts.edit", "Edit Broadcast") : t("broadcasts.create", "Create a New Broadcast")}
                  </h3>
                </Col>
                <Col className="text-right" xs="4">
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => navigate("/admin/broadcasts")}
                  >
                    {t("page.messages.back", "Back")}
                  </Button>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <Form>
                <h6 className="heading-small text-muted mb-4">
                  {t("broadcasts.form.section", "Broadcast Information")}
                </h6>

                <div className="pl-lg-4">
                  <Row>
                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label">
                          {t("broadcasts.fields.title", "Title")}
                        </label>
                        <Input
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          type="text"
                        />
                      </FormGroup>
                    </Col>

                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label">
                          {t("broadcasts.fields.subject", "Email Subject")}
                        </label>
                        <Input
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-control-label">
                          {t("broadcasts.fields.content", "Message Content")}
                        </label>
                        <ReactQuill
                          theme="snow"
                          value={formData.htmlContent}
                          onChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              htmlContent: value,
                            }))
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="align-items-center mb-3">
                    <Col md="3">
                      <FormGroup check>
                        <label className="form-check-label">
                          <Input
                            type="checkbox"
                            name="showRecipients"
                            checked={formData.showRecipients}
                            onChange={handleChange}
                          />
                          {" "}{t("broadcasts.fields.selectRecipients", "Select Recipients")}
                        </label>
                      </FormGroup>
                    </Col>

                    {formData.showRecipients && (
                      <>
                        <Col md="3">
                          <FormGroup check>
                            <label className="form-check-label">
                              <Input
                                type="checkbox"
                                name="useUsers"
                                checked={formData.useUsers}
                                onChange={handleChange}
                              />
                              {" "}{t("broadcasts.fields.users", "Targeted Users")}
                            </label>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup check>
                            <label className="form-check-label">
                              <Input
                                type="checkbox"
                                name="useRoles"
                                checked={formData.useRoles}
                                onChange={handleChange}
                              />
                              {" "}{t("broadcasts.fields.roles", "Target Roles")}
                            </label>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup check>
                            <label className="form-check-label">
                              <Input
                                type="checkbox"
                                name="useCustomEmails"
                                checked={formData.useCustomEmails}
                                onChange={handleChange}
                              />
                              {" "}{t("broadcasts.fields.customEmails", "Custom Emails")}
                            </label>
                          </FormGroup>
                        </Col>
                      </>
                    )}
                  </Row>

                  {formData.showRecipients && (
                    <Row>
                      {formData.useUsers && (
                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label">
                              {t("broadcasts.fields.users", "Targeted Users")}
                            </label>

                            <Select
                              isMulti
                              isDisabled={loadingUsers}
                              options={users.map((user) => ({
                                value: user._id,
                                label: `${user.firstName} ${user.lastName} (${user.email})`,
                              }))}
                              value={formData.targetUsers.map((id) => {
                                const user = users.find((u) => u._id === id);
                                return user
                                  ? {
                                      value: user._id,
                                      label: `${user.firstName} ${user.lastName} (${user.email})`,
                                    }
                                  : null;
                              }).filter(Boolean)}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  targetUsers: selected
                                    ? selected.map((opt) => opt.value)
                                    : [],
                                }))
                              }
                              placeholder={t("broadcasts.fields.selectUsers", "Select specific users")}
                              classNamePrefix="react-select"
                            />
                          </FormGroup>
                        </Col>
                      )}

                      {formData.useRoles && (
                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label">
                              {t("broadcasts.fields.roles", "Target Roles")}
                            </label>

                            <Select
                              isMulti
                              isDisabled={loadingRoles}
                              options={roles.map((role) => ({
                                value: role.name,
                                label: t(helpers.ROLE_LABELS[role.name] || role.name),
                              }))}
                              value={formData.targetRoles.map((role) => ({
                                value: role,
                                label: t(helpers.ROLE_LABELS[role] || role),
                              }))}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  targetRoles: selected
                                    ? selected.map((opt) => opt.value)
                                    : [],
                                }))
                              }
                              placeholder={t("broadcasts.fields.selectRoles", "Select Roles")}
                              classNamePrefix="react-select"
                            />
                          </FormGroup>
                        </Col>
                      )}

                      {formData.useCustomEmails && (
                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label">
                              {t("broadcasts.fields.customEmails", "Custom Emails")}
                            </label>

                            <CreatableSelect
                              isMulti
                              value={formData.customEmails.map((email) => ({
                                value: email,
                                label: email,
                              }))}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customEmails: selected
                                    ? selected.map((opt) => opt.value)
                                    : [],
                                }))
                              }
                              placeholder={t("broadcasts.fields.customEmailsPlaceholder", "Type email and press enter...")}
                              classNamePrefix="react-select"
                            />
                          </FormGroup>
                        </Col>
                      )}
                    </Row>
                  )}
                  
                  <FormGroup check className="mb-3">
                    <label className="form-check-label">
                      <Input
                        type="checkbox"
                        name="sendNow"
                        checked={formData.sendNow}
                        onChange={handleChange}
                        disabled={formData.isScheduled}
                      />
                      {" "}{t("broadcasts.fields.sendNow", "Send immediately after saving")}
                    </label>
                  </FormGroup>

                  <Row>
                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label">
                          {t("broadcasts.fields.schedule", "Schedule Date & Time")}
                        </label>
                        <Input
                          type="datetime-local"
                          name="scheduledAt"
                          value={formData.scheduledAt || ""}
                          onChange={handleChange}
                          disabled={formData.sendNow === true}
                        />
                        <FormGroup check className="mt-3">
                          <label className="form-check-label">
                            <Input
                              type="checkbox"
                              name="isScheduled"
                              checked={formData.isScheduled}
                              onChange={handleChange}
                              disabled={formData.sendNow}
                            />{" "}
                            {t("broadcasts.fields.scheduleCheck", "Send at scheduled time")}
                          </label>
                        </FormGroup>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      color="warning"
                      size="md"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting && <Spinner size="sm" className="mr-2" />}
                      {isEditMode ? t("button.update", "Update") : t("button.save", "Save")}
                    </Button>
                  </div>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BroadcastForm;
